import React from 'react';
import { motion } from 'framer-motion';
import { useParams, Link } from 'react-router-dom';
import { Music, MapPin, Calendar, Download, Send, ExternalLink, Instagram, Twitter, Youtube } from 'lucide-react';
export function PublicArtistBookingPage() {
  const {
    artistSlug
  } = useParams();
  return <div className="min-h-screen bg-background text-textMain" data-id="element-6844">
      {/* Minimal Header */}
      <header className="h-16 border-b border-border flex items-center justify-between px-6 bg-surface/50 backdrop-blur sticky top-0 z-50" data-id="element-6845">
        <Link to="/" className="flex items-center gap-2" data-id="element-6846">
          <span className="text-xl font-bold" data-id="element-6847">Booker</span>
          <div className="w-1.5 h-1.5 rounded-full bg-primary" data-id="element-6848"></div>
        </Link>
        <div className="text-sm text-textMuted" data-id="element-6849">Powered by Booker</div>
      </header>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="max-w-5xl mx-auto px-6 py-12" data-id="element-6850">
        {/* Hero Section */}
        <div className="flex flex-col md:flex-row gap-8 mb-12" data-id="element-6851">
          <div className="w-full md:w-1/3 aspect-square rounded-2xl bg-surface border border-border flex items-center justify-center overflow-hidden shrink-0" data-id="element-6852">
            <div className="text-6xl font-bold text-primary/20" data-id="element-6853">TW</div>
          </div>
          <div className="flex-1 flex flex-col justify-center" data-id="element-6854">
            <div className="flex flex-wrap gap-2 mb-4" data-id="element-6855">
              <span className="px-3 py-1 bg-surface border border-border rounded-full text-xs font-medium" data-id="element-6856">
                Folk Rock
              </span>
              <span className="px-3 py-1 bg-surface border border-border rounded-full text-xs font-medium" data-id="element-6857">
                Americana
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-6858">
              The Wildwoods
            </h1>
            <p className="text-lg text-textMuted mb-6 max-w-2xl leading-relaxed" data-id="element-6859">
              An enchanting folk/Americana trio known for their tight vocal
              harmonies, intricate instrumentation, and captivating live
              performances. Based in Lincoln, NE.
            </p>
            <div className="flex flex-wrap items-center gap-4" data-id="element-6860">
              <button className="px-6 py-3 bg-primary text-background rounded-xl font-medium hover:bg-primaryHover transition-colors flex items-center gap-2" data-id="element-6861">
                <Send className="w-4 h-4" data-id="element-6862" /> Send Inquiry
              </button>
              <button className="px-6 py-3 bg-surface border border-border rounded-xl font-medium hover:bg-surfaceHover transition-colors flex items-center gap-2" data-id="element-6863">
                <Download className="w-4 h-4" data-id="element-6864" /> Download EPK
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-6865">
          {/* Main Content */}
          <div className="md:col-span-2 space-y-8" data-id="element-6866">
            <section data-id="element-6867">
              <h2 className="text-2xl font-bold mb-4" data-id="element-6868">About</h2>
              <div className="prose prose-invert max-w-none text-textMuted" data-id="element-6869">
                <p data-id="element-6870">
                  The Wildwoods bring a pioneering acoustic sound to the
                  Americana/Folk scene. Their music is heavily inspired by
                  nature and the landscapes of their home state, blending
                  traditional folk elements with modern songwriting.
                </p>
                <p className="mt-4" data-id="element-6871">
                  They have supported acts like The Lumineers, Trampled by
                  Turtles, and The Head and The Heart.
                </p>
              </div>
            </section>

            <section data-id="element-6872">
              <h2 className="text-2xl font-bold mb-4" data-id="element-6873">Upcoming Public Dates</h2>
              <div className="bg-surface border border-border rounded-xl overflow-hidden" data-id="element-6874">
                <div className="divide-y divide-border" data-id="element-6875">
                  {[{
                  date: 'Jun 12, 2026',
                  venue: 'The Empty Bottle',
                  city: 'Chicago, IL'
                }, {
                  date: 'Jun 14, 2026',
                  venue: 'Beachland Tavern',
                  city: 'Cleveland, OH'
                }, {
                  date: 'Jun 22, 2026',
                  venue: 'The Sinclair',
                  city: 'Cambridge, MA'
                }].map((show, i) => <div key={i} className="p-4 flex items-center justify-between hover:bg-surfaceHover/50 transition-colors" data-id="element-6876">
                      <div className="flex items-center gap-4" data-id="element-6877">
                        <div className="text-center shrink-0 w-12" data-id="element-6878">
                          <div className="text-xs text-textMuted uppercase" data-id="element-6879">
                            Jun
                          </div>
                          <div className="text-lg font-bold" data-id="element-6880">
                            {show.date.split(' ')[1].replace(',', '')}
                          </div>
                        </div>
                        <div data-id="element-6881">
                          <div className="font-medium" data-id="element-6882">{show.venue}</div>
                          <div className="text-sm text-textMuted" data-id="element-6883">
                            {show.city}
                          </div>
                        </div>
                      </div>
                      <button className="px-4 py-1.5 bg-background border border-border rounded-lg text-sm font-medium hover:text-textMain transition-colors" data-id="element-6884">
                        Tickets
                      </button>
                    </div>)}
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6" data-id="element-6885">
            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-6886">
              <h3 className="font-semibold mb-4" data-id="element-6887">Booking Details</h3>
              <div className="space-y-4 text-sm" data-id="element-6888">
                <div className="flex items-start gap-3" data-id="element-6889">
                  <Users className="w-4 h-4 text-textMuted shrink-0 mt-0.5" data-id="element-6890" />
                  <div data-id="element-6891">
                    <div className="font-medium" data-id="element-6892">Lineup Size</div>
                    <div className="text-textMuted" data-id="element-6893">
                      3-piece (Guitar, Violin, Bass)
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-6894">
                  <MapPin className="w-4 h-4 text-textMuted shrink-0 mt-0.5" data-id="element-6895" />
                  <div data-id="element-6896">
                    <div className="font-medium" data-id="element-6897">Home Base</div>
                    <div className="text-textMuted" data-id="element-6898">Lincoln, NE</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-surface border border-border rounded-xl p-6" data-id="element-6899">
              <h3 className="font-semibold mb-4" data-id="element-6900">Links</h3>
              <div className="space-y-3" data-id="element-6901">
                <a href="#" className="flex items-center gap-3 text-sm text-textMuted hover:text-textMain transition-colors" data-id="element-6902">
                  <Globe className="w-4 h-4" data-id="element-6903" /> Official Website
                </a>
                <a href="#" className="flex items-center gap-3 text-sm text-textMuted hover:text-textMain transition-colors" data-id="element-6904">
                  <Instagram className="w-4 h-4" data-id="element-6905" /> Instagram
                </a>
                <a href="#" className="flex items-center gap-3 text-sm text-textMuted hover:text-textMain transition-colors" data-id="element-6906">
                  <Youtube className="w-4 h-4" data-id="element-6907" /> YouTube
                </a>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>;
}
function Globe(props: any) {
  return <ExternalLink {...props} data-id="element-6908" />;
}